/**
 * The client-tool bridge: holds a WebSocket open to the instance, receives
 * forwarded tool requests, runs them through the permission engine + safety
 * guard, executes the approved ones on the host, and returns results.
 */
import type { ApiClient } from "./api.ts";
import type { HostTools } from "./host-tools.ts";
import { decide, isCatastrophic, type PermissionState } from "./permissions.ts";
import { saveApprovals } from "./approvals.ts";
import { Heartbeat } from "./heartbeat.ts";
import type { HostResult, ToolRequest } from "./types.ts";
import { diffLines, newFileLines, highlightBash, oneLineCommand } from "./render.ts";

export type ApprovalChoice = "allow" | "deny" | "always" | "always_risk";
/** Approval outcome plus, for a denial, an optional reason the user typed for the agent. */
export type ApprovalResult = { choice: ApprovalChoice; reason?: string };

export type ConnectionState = "connected" | "reconnecting" | "reconnected";

export interface BridgeHooks {
  /** Print a permanent line (a finished/denied/blocked tool). `detail` lines are
   * pre-styled extra rows (e.g. a truncated diff) printed verbatim beneath it. */
  onActivity(line: string, detail?: string[]): void;
  /** Update the live working status (the currently-running tool, or null = idle). */
  /** A host tool started (`summary`) or finished (`null`) executing, keyed by its
   * tool-call id. The CLI is the source of truth for what is running on the host —
   * the server's stream events can be missed (reconnects), leaving a stale label. */
  onStatus?(id: string, summary: string | null): void;
  /** Report connection lifecycle so the UI can show disconnect/reconnect state. */
  onConnection?(state: ConnectionState, attempt: number): void;
  /** Ask the user to approve a tool; resolve with their choice. */
  requestApproval(req: ToolRequest, summary: string, effectiveRisk: number): Promise<ApprovalResult>;
}

/** Tools that mutate or execute (used to deny in plan mode cleanly). */
const PATH_ARG_TOOLS = new Set(["read_file", "grep", "glob", "write_file", "edit_file"]);

export class Bridge {
  private ws: WebSocket | null = null;
  private closed = false;
  private heartbeat: Heartbeat | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private resolveConnected: (() => void) | null = null;
  // Durable forwarded calls we've started handling, so a server re-send (after a
  // reconnect) doesn't prompt or run them twice.
  private handledDurable = new Set<string>();

  constructor(
    private api: ApiClient,
    private threadId: string,
    private host: HostTools,
    private perm: PermissionState,
    private hooks: BridgeHooks
  ) {}

  /**
   * Connect and keep the bridge connected. Resolves on the first successful
   * open; thereafter any drop is reconnected automatically with exponential
   * backoff (disconnections are expected — e.g. a dev-server reload — so this
   * must be rock solid). A short safety timeout resolves startup even if the
   * very first attempt is slow, since reconnection continues in the background.
   */
  connect(): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      this.resolveConnected = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      // Don't hang startup forever if the first attempt is slow.
      setTimeout(() => this.resolveConnected?.(), 8000);
      this.openSocket();
    });
  }

  private openSocket(): void {
    if (this.closed) return;
    const url = `${this.api.wsEndpoint}/api/threads/${this.threadId}/bridge?token=${encodeURIComponent(this.api.bearer)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      const wasReconnecting = this.reconnectAttempt > 0;
      this.reconnectAttempt = 0;
      this.startHeartbeat(ws);
      this.hooks.onConnection?.(wasReconnecting ? "reconnected" : "connected", 0);
      this.resolveConnected?.();
    });

    ws.addEventListener("message", (ev) => {
      // Any frame (pong, bridge_ready, tool_request) proves the link is alive.
      if (this.ws === ws) this.heartbeat?.markAlive();
      this.onMessage(String((ev as MessageEvent).data));
    });

    // A socket that fails to open fires 'error' but may never fire 'close', so
    // both must drive reconnection. handleDrop dedupes via the current-socket check.
    ws.addEventListener("error", () => this.handleDrop(ws));
    ws.addEventListener("close", () => this.handleDrop(ws));
  }

  private handleDrop(ws: WebSocket): void {
    if (this.ws !== ws) return; // stale event from a superseded socket
    this.ws = null;
    this.stopHeartbeat();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer) return;
    this.reconnectAttempt++;
    this.hooks.onConnection?.("reconnecting", this.reconnectAttempt);
    // Exponential backoff with jitter, capped — keep trying indefinitely.
    const base = Math.min(500 * 2 ** (this.reconnectAttempt - 1), 15000);
    const delay = base + Math.floor(Math.random() * 400);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private startHeartbeat(ws: WebSocket): void {
    this.stopHeartbeat();
    // Heartbeat so the server can tell a live client (mid-build or awaiting an
    // approval) from a dead one and never freeze a thread on us — and so WE
    // notice when the server goes silent (a half-open TCP that never fires
    // 'close'/'error') and reconnect instead of looking connected forever.
    this.heartbeat = new Heartbeat(ws, () => this.handleDrop(ws));
    this.heartbeat.start();
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      this.heartbeat.stop();
      this.heartbeat = null;
    }
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }

  private send(payload: object): void {
    try {
      this.ws?.send(JSON.stringify(payload));
    } catch {
      // socket gone; the server will time the call out
    }
  }

  private async onMessage(raw: string): Promise<void> {
    let msg: any;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg.type !== "tool_request") return;
    const req = msg as ToolRequest;
    await this.handleToolRequest(req);
  }

  /**
   * Reply to a tool request. Durable calls (the agent parked them) deliver the
   * result over HTTP so it lands even if this socket later drops; legacy calls
   * reply over the WebSocket.
   */
  private respond(req: ToolRequest, ok: boolean, result?: string, error?: string): void {
    if (req.durable && req.toolCallId) {
      void this.api.postToolResult(this.threadId, req.toolCallId, ok, result, error);
    } else {
      this.send({ type: "tool_response", id: req.id, ok, result, error });
    }
  }

  private async handleToolRequest(req: ToolRequest): Promise<void> {
    // Durable calls may be re-sent by the server after a reconnect — only handle
    // each once (prompt + run), so the user isn't asked twice.
    if (req.durable && req.toolCallId) {
      if (this.handledDurable.has(req.toolCallId)) return;
      this.handledDurable.add(req.toolCallId);
    }

    const summary = describe(req);

    // Compute an effective risk, escalating anything that touches paths outside
    // the project directory (the agent is told to stay inside it).
    let effectiveRisk = typeof req.risk === "number" ? req.risk : req.requestPermission ? 3 : 1;
    if (PATH_ARG_TOOLS.has(req.tool) && this.host.isOutsideProject(req.args.path as string | undefined)) {
      effectiveRisk = Math.max(effectiveRisk, 4);
    }

    // Hard safety: never run catastrophic shell commands, even in bypass mode.
    if (req.tool === "bash" && isCatastrophic(String(req.args.command || ""))) {
      this.hooks.onActivity(`⛔ blocked dangerous command: ${summary}`);
      this.respond(req, false, undefined, "Blocked: this command is considered catastrophic and was refused by the client safety guard.");
      return;
    }

    // Permission key: normally the tool name, but MCP calls are gated at the
    // finer `mcp:<server>/<tool>` granularity so "always allow this tool" approves
    // one server's tool rather than every MCP call.
    const permKey = permissionKey(req);
    const decision = decide(this.perm, permKey, effectiveRisk, !!req.requestPermission);

    if (decision === "deny") {
      this.hooks.onActivity(`⛔ ${summary} — blocked (risk ${effectiveRisk})`);
      this.respond(req, false, undefined, `Denied by policy (risk ${effectiveRisk}).`);
      return;
    }

    if (decision === "ask") {
      const { choice, reason } = await this.hooks.requestApproval(req, summary, effectiveRisk);
      if (choice === "deny") {
        const why = reason?.trim();
        this.hooks.onActivity(`⛔ ${summary} — you declined${why ? `: ${why}` : ""}`);
        this.respond(
          req,
          false,
          undefined,
          why
            ? `The user declined to run this operation. Their reason: ${why}`
            : "The user declined to run this operation."
        );
        return;
      }
      if (choice === "always") this.perm.alwaysAllow.add(permKey);
      if (choice === "always_risk") this.perm.allowRisk.add(effectiveRisk);
      if (choice === "always" || choice === "always_risk") {
        saveApprovals(this.api, this.threadId, this.perm);
      }
    }

    // Show the running tool in the live status, then leave a permanent line. The
    // status is keyed by the tool-call id and cleared in a `finally`, so the
    // "Working …" label can never get stuck on a tool that already finished even
    // if execution throws or the server's done event is dropped.
    const callKey = req.toolCallId ?? req.id ?? `${req.tool}:${summary}`;
    this.hooks.onStatus?.(callKey, summary);
    let result: HostResult;
    try {
      result = await this.host.execute(req.tool, req.args);
    } finally {
      this.hooks.onStatus?.(callKey, null);
    }
    if (result.ok) {
      const { display, detail } = activityPresentation(req, summary);
      this.hooks.onActivity(`✓ ${display}${detailSuffix(req.tool, result.result)}`, detail);
      this.respond(req, true, result.result ?? "");
    } else {
      this.hooks.onActivity(`✗ ${summary} — ${result.error}`);
      this.respond(req, false, undefined, result.error);
    }
  }
}

/**
 * Stable permission key for a request. For MCP, gate on the specific server +
 * tool/resource so approvals are fine-grained; for everything else, the tool name.
 */
export function permissionKey(req: ToolRequest): string {
  if (req.tool !== "mcp") return req.tool;
  const a = req.args;
  const server = String(a.server || "?");
  const action = String(a.action || "call");
  if (action === "read_resource") return `mcp:${server}/resource`;
  if (action === "list_tools") return `mcp:${server}/list`;
  return `mcp:${server}/${String(a.tool || "?")}`;
}

const DIM = "\x1b[2m";
const RESET = "\x1b[0m";

/**
 * Activity-feed presentation for a finished tool call: the ✓-line text plus
 * optional indented sub-lines. Bash gets its command syntax-highlighted;
 * edits/writes get a truncated git-style hunk beneath the summary so the
 * change is visible at a glance; background_process reads as its action with
 * the command/description as a dim sub-line — never raw JSON args.
 */
export function activityPresentation(
  req: ToolRequest,
  summary: string
): { display: string; detail?: string[] } {
  const a = req.args;
  const cols = Math.max(40, process.stdout.columns || 80);
  if (req.tool === "bash") {
    return { display: `bash: ${highlightBash(oneLineCommand(String(a.command ?? ""), cols - 20))}` };
  }
  if (req.tool === "edit_file") {
    return { display: summary, detail: diffLines(String(a.old_string ?? ""), String(a.new_string ?? "")) };
  }
  if (req.tool === "write_file") {
    return { display: summary, detail: newFileLines(String(a.content ?? "")) };
  }
  if (req.tool === "background_process") {
    const action = String(a.action || "list");
    const id = a.id ? ` ${String(a.id)}` : "";
    const display = action === "start" ? "bg start" : `bg ${action}${id}`;
    const detail: string[] = [];
    if (action === "start" && a.command) {
      detail.push(`    ${DIM}${oneLineCommand(String(a.command), cols - 8)}${RESET}`);
    }
    if (typeof a.description === "string" && a.description.trim()) {
      detail.push(`    ${DIM}${oneLineCommand(a.description, cols - 8)}${RESET}`);
    }
    return { display, detail: detail.length ? detail : undefined };
  }
  return { display: summary };
}

/** One-line human summary of a tool request for the activity feed. */
export function describe(req: ToolRequest): string {
  const a = req.args;
  switch (req.tool) {
    case "mcp": {
      const server = String(a.server || "?");
      const action = String(a.action || "call");
      if (action === "list_tools") return `mcp ${server}: list tools`;
      if (action === "read_resource") return `mcp ${server}: read ${a.uri}`;
      return `mcp ${server}: ${a.tool}`;
    }
    case "read_file":
      return `read ${a.path}`;
    case "grep":
      return `grep "${a.pattern}"${a.glob ? ` in ${a.glob}` : ""}`;
    case "glob":
      return `find ${a.pattern}`;
    case "write_file":
      return `write ${a.path}`;
    case "edit_file":
      return `edit ${a.path}`;
    case "save_to_disk":
      return `save ${a.source_path} → ${a.dest_path}`;
    case "run_skill_script":
      return `skill ${a.skill}: run ${a.entry}`;
    case "bash":
      return `bash: ${oneLineCommand(String(a.command ?? ""), 80)}`;
    case "background_process": {
      // The approval prompt sees only this summary, so `start` must carry the
      // command; the activity feed re-presents it as an indented sub-line.
      const action = String(a.action || "list");
      if (action === "start") return `bg start: ${oneLineCommand(String(a.command ?? ""), 80)}`;
      if (action === "list") return "bg list";
      return `bg ${action}${a.id ? ` ${String(a.id)}` : ""}`;
    }
    default:
      return `${req.tool} ${JSON.stringify(a).slice(0, 80)}`;
  }
}

/** A short parenthetical hint appended to a finished read-style tool line. */
function detailSuffix(tool: string, result?: string): string {
  if (!result) return "";
  // Mutations already read clearly from their summary; don't pile on.
  if (tool === "write_file" || tool === "edit_file") return "";
  if (tool === "bash") {
    const m = result.match(/\[exit code (\d+)\]\s*$/);
    return m ? ` (exit ${m[1]})` : "";
  }
  const lines = result.split("\n").length;
  return ` (${lines} line${lines === 1 ? "" : "s"})`;
}
